/**
 * Passport Education Section
 */

import { useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EducationAttestationBadge, EDUCATION_KIND_LABEL, educationStudyStatus, type EducationKind } from './EducationAttestationBadge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { GraduationCap, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { parseDateOnly, localTodayIso } from '@/lib/passport/dateOnly'; // Core-100 R4 W1: date-only facts in the local calendar
import {
  usePassportEducation, useCreateEducation, useUpdateEducation, useDeleteEducation,
} from '@/hooks/usePassportSections';

interface Props {
  userId: string;
  isOwner: boolean;
  isReadOnly?: boolean;
}

interface FormData {
  school_name: string;
  program: string;
  degree: string;
  start_date: string;
  end_date: string;
  graduated: boolean;
  notes: string;
  education_kind: EducationKind | '';
}

const emptyForm: FormData = {
  school_name: '', program: '', degree: '', start_date: '', end_date: '', graduated: false, notes: '', education_kind: '',
};

export function PassportEducationSection({ userId, isOwner, isReadOnly }: Props) {
  // FIX-R41-MED-1: pass isOwner so non-owners get the public-safe column
  // subset (no `notes` free-text). Consistent with references / eligibility
  // / experience sections.
  const { data: items, isLoading, isError } = usePassportEducation(userId, { isOwner });
  const createItem = useCreateEducation();
  const updateItem = useUpdateEducation();
  const deleteItem = useDeleteEducation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setForm({
      school_name: item.school_name,
      program: item.program || '',
      degree: item.degree || '',
      start_date: item.start_date || '',
      end_date: item.end_date || '',
      graduated: item.graduated ?? false,
      notes: item.notes || '',
      education_kind: item.education_kind || '',
    });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.school_name.trim()) return;
    const payload = {
      school_name: form.school_name.trim(),
      program: form.program.trim() || undefined,
      degree: form.degree.trim() || undefined,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      graduated: form.graduated,
      notes: form.notes.trim() || undefined,
      education_kind: form.education_kind || null,
    };
    if (editingId) {
      updateItem.mutate({ id: editingId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    } else {
      createItem.mutate({ user_id: userId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const formatDateRange = (start?: string | null, end?: string | null) => {
    const parts: string[] = [];
    if (start) parts.push(format(parseDateOnly(start), 'yyyy'));
    if (end) parts.push(format(parseDateOnly(end), 'yyyy'));
    return parts.join(' – ') || null;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Education</CardTitle>
            {items && <Badge variant="secondary" className="text-xs">{items.length}</Badge>}
          </div>
          {isOwner && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* CORE-C GAP-003: a failed read is not a statement that the person
            has none. Checked BEFORE the empty branch, which is where the
            failure used to land. */}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <PassportSectionFallback sectionName="Education" inline />
        ) : !items?.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {isOwner ? 'No education entries yet.' : 'No education to display.'}
          </p>
        ) : (
          items.map((item) => (
            <div key={item.id} className="border rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{item.school_name}</p>
                  {(item.degree || item.program) && (
                    <p className="text-xs text-muted-foreground">
                      {[item.degree, item.program].filter(Boolean).join(' — ')}
                    </p>
                  )}
                  {(() => { const r = formatDateRange(item.start_date, item.end_date); return r ? <p className="text-xs text-muted-foreground">{r}</p> : null; })()}
                  {/* Core-100 R2 (CORE-M): kind, study status (never completed-as-ongoing) and the school's word, if any. */}
                  <div className="flex flex-wrap gap-1">
                    {(item as { education_kind?: EducationKind | null }).education_kind && (
                      <Badge variant="outline" className="text-[10px] mt-1">{EDUCATION_KIND_LABEL[(item as { education_kind: EducationKind }).education_kind]}</Badge>
                    )}
                    {(() => {
                      const st = educationStudyStatus(item.start_date, item.end_date, item.graduated, localTodayIso());
                      const label = st === 'completed' ? 'Graduated' : st === 'current' ? 'Currently studying' : st === 'ended' ? 'Ended' : null;
                      return label ? <Badge variant="secondary" className="text-[10px] mt-1">{label}</Badge> : null;
                    })()}
                    {(item as { attested_by_verification_id?: string | null }).attested_by_verification_id && <EducationAttestationBadge educationId={item.id} />}
                  </div>
                </div>
                {isOwner && !isReadOnly && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteItem.mutate({ id: item.id, userId })}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Education' : 'Add Education'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>School Name *</Label>
              <Input value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="education-kind">Kind of education</Label>
              <select id="education-kind" className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.education_kind} onChange={(e) => setForm({ ...form, education_kind: e.target.value as EducationKind | '' })}>
                <option value="">Not specified</option>
                {(Object.keys(EDUCATION_KIND_LABEL) as EducationKind[]).map((k) => <option key={k} value={k}>{EDUCATION_KIND_LABEL[k]}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Program</Label>
              <Input value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} placeholder="e.g. Automotive Technology" />
            </div>
            <div className="space-y-1.5">
              <Label>Degree</Label>
              <Input value={form.degree} onChange={(e) => setForm({ ...form, degree: e.target.value })} placeholder="e.g. Associate" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="graduated" checked={form.graduated} onCheckedChange={(c) => setForm({ ...form, graduated: !!c })} />
              <Label htmlFor="graduated" className="font-normal cursor-pointer">Graduated</Label>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.school_name.trim() || createItem.isPending || updateItem.isPending}>
              {(createItem.isPending || updateItem.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
